"use client";

import {
  Bookmark,
  ChevronRight,
  Eye,
  Globe2,
  Loader2,
  MoreHorizontal,
  Plus,
  Star,
  Trash2,
  UsersRound,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Popover } from "@/components/ui/Popover";
import {
  createSavedViewDefinition,
  savedViewUrl,
  type TaskViewDescriptor,
} from "@/lib/savedTaskViews";
import type {
  SavedViewDefinition,
  SavedViewVisibility,
  Team,
} from "@/lib/types";
import { cn, formatRelative } from "@/lib/utils";

type SavedViewRow = {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  owner_id: string;
  visibility: SavedViewVisibility;
  team_id: string | null;
  definition: SavedViewDefinition;
  updated_at: string;
};

type SavedViewPreferenceRow = {
  saved_view_id: string;
  is_favorite: boolean;
  is_default: boolean;
  subscribed: boolean;
  position: number;
};

type SavedViewPayload = {
  views: SavedViewRow[];
  preferences: SavedViewPreferenceRow[];
};

export const SAVED_VIEWS_CHANGED_EVENT = "pauta:saved-views-changed";

function useSavedViews(workspaceId: string | null) {
  const [payload, setPayload] = useState<SavedViewPayload>({
    views: [],
    preferences: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!workspaceId) {
      setPayload({ views: [], preferences: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/views`, {
        cache: "no-store",
      });
      if (!response.ok) throw new Error("load_failed");
      const next = (await response.json()) as SavedViewPayload;
      setPayload({
        views: Array.isArray(next.views) ? next.views : [],
        preferences: Array.isArray(next.preferences) ? next.preferences : [],
      });
    } catch {
      setError("Saved views could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
    const onChanged = () => void refresh();
    window.addEventListener(SAVED_VIEWS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SAVED_VIEWS_CHANGED_EVENT, onChanged);
  }, [refresh]);

  return { ...payload, loading, error, refresh };
}

function preferenceMap(preferences: SavedViewPreferenceRow[]) {
  return new Map(preferences.map((preference) => [preference.saved_view_id, preference]));
}

function visibilityLabel(visibility: SavedViewVisibility) {
  if (visibility === "personal") return "Only you";
  if (visibility === "team") return "Team";
  return "Workspace";
}

function visibilityIcon(visibility: SavedViewVisibility) {
  if (visibility === "personal") return <Eye size={12} />;
  if (visibility === "team") return <UsersRound size={12} />;
  return <Globe2 size={12} />;
}

function openSavedView(view: SavedViewRow, viewerId: string) {
  const next = savedViewUrl(window.location.href, view.definition, viewerId);
  window.location.assign(next);
}

export function SavedViewFavorites({
  workspaceId,
  currentUserId,
}: {
  workspaceId: string | null;
  currentUserId: string | null;
}) {
  const { views, preferences, loading } = useSavedViews(workspaceId);
  const byView = useMemo(() => preferenceMap(preferences), [preferences]);
  const favorites = useMemo(
    () =>
      views
        .filter((view) => byView.get(view.id)?.is_favorite)
        .sort(
          (a, b) =>
            (byView.get(a.id)?.position ?? 0) -
            (byView.get(b.id)?.position ?? 0)
        )
        .slice(0, 3),
    [byView, views]
  );

  if (loading || !currentUserId || favorites.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--fg-muted)]">
        Saved
      </div>
      <div className="flex flex-col gap-0.5">
        {favorites.map((view) => (
          <button
            key={view.id}
            type="button"
            onClick={() => openSavedView(view, currentUserId)}
            className="group flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[12.5px] text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
          >
            <span className="w-4 shrink-0 text-center text-[12px]">
              {view.icon || "◫"}
            </span>
            <span className="min-w-0 flex-1 truncate">{view.name}</span>
            <ChevronRight
              size={11}
              className="text-[var(--fg-muted)] opacity-0 transition-opacity group-hover:opacity-100"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export function SaveCurrentViewButton({
  workspaceId,
  currentUserId,
  descriptor,
  teams,
  canShare,
}: {
  workspaceId: string;
  currentUserId: string | null;
  descriptor: TaskViewDescriptor;
  teams: Team[];
  canShare: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="focus-ring inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2 text-[11.5px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
        title="Save this view"
      >
        <Bookmark size={12} />
        <span className="hidden lg:inline">Save view</span>
      </button>
      <SaveViewDialog
        open={open}
        onClose={() => setOpen(false)}
        workspaceId={workspaceId}
        currentUserId={currentUserId}
        descriptor={descriptor}
        teams={teams}
        canShare={canShare}
      />
    </>
  );
}

function SaveViewDialog({
  open,
  onClose,
  workspaceId,
  currentUserId,
  descriptor,
  teams,
  canShare,
}: {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  currentUserId: string | null;
  descriptor: TaskViewDescriptor;
  teams: Team[];
  canShare: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] =
    useState<SavedViewVisibility>("personal");
  const [teamId, setTeamId] = useState(descriptor.teamId ?? teams[0]?.id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const visibilityOptions: SavedViewVisibility[] = canShare
    ? teams.length > 0
      ? ["personal", "team", "workspace"]
      : ["personal", "workspace"]
    : ["personal"];

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setVisibility("personal");
    setTeamId(descriptor.teamId ?? teams[0]?.id ?? "");
    setError(null);
  }, [descriptor.teamId, open, teams]);

  async function save() {
    const cleanName = name.trim();
    if (!cleanName || !currentUserId) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/views`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: cleanName,
          description: description.trim(),
          icon: "◫",
          visibility,
          team_id: visibility === "team" ? teamId : null,
          definition: createSavedViewDefinition(descriptor, visibility),
          favorite: true,
        }),
      });
      if (!response.ok) throw new Error("save_failed");
      window.dispatchEvent(new Event(SAVED_VIEWS_CHANGED_EVENT));
      onClose();
    } catch {
      setError("Pauta could not save this view. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} width={460}>
      <div className="border-b border-[var(--border)] px-5 py-4">
        <h2 className="text-[15.5px] font-medium tracking-tight">Save task view</h2>
        <p className="mt-1 text-[11.5px] text-[var(--fg-tertiary)]">
          Keep this scope, layout, search, and every active filter.
        </p>
      </div>
      <form
        className="space-y-4 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="block">
          <span className="mb-1.5 block text-[11.5px] font-medium text-[var(--fg-secondary)]">
            Name
          </span>
          <input
            autoFocus
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Migration verification"
            className="focus-ring h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 text-[12.5px] outline-none placeholder:text-[var(--fg-muted)]"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[11.5px] font-medium text-[var(--fg-secondary)]">
            Description
          </span>
          <input
            maxLength={240}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What this view is useful for"
            className="focus-ring h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 text-[12.5px] outline-none placeholder:text-[var(--fg-muted)]"
          />
        </label>
        <div>
          <span className="mb-1.5 block text-[11.5px] font-medium text-[var(--fg-secondary)]">
            Who can open it
          </span>
          <div
            className={cn(
              "grid gap-2",
              visibilityOptions.length === 1
                ? "grid-cols-1"
                : visibilityOptions.length === 2
                  ? "grid-cols-2"
                  : "grid-cols-3"
            )}
          >
            {visibilityOptions.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setVisibility(value)}
                className={cn(
                  "focus-ring flex h-9 items-center justify-center gap-1.5 rounded-md border px-2 text-[11.5px] font-medium transition-colors disabled:opacity-40",
                  visibility === value
                    ? "border-[var(--accent)] bg-[var(--accent-bg)] text-[var(--accent)]"
                    : "border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]"
                )}
              >
                {visibilityIcon(value)}
                {visibilityLabel(value)}
              </button>
            ))}
          </div>
          {!canShare && (
            <p className="mt-2 text-[10.5px] leading-4 text-[var(--fg-muted)]">
              Personal views are available to everyone. Workspace editors can
              also share views with a team or the workspace.
            </p>
          )}
        </div>
        {visibility === "team" && (
          <label className="block">
            <span className="mb-1.5 block text-[11.5px] font-medium text-[var(--fg-secondary)]">
              Team
            </span>
            <select
              value={teamId}
              onChange={(event) => setTeamId(event.target.value)}
              className="focus-ring h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 text-[12.5px] outline-none"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {error && (
          <p className="rounded-md border border-[var(--red)]/25 bg-[var(--red)]/10 px-3 py-2 text-[11.5px] text-[var(--red)]">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-8 rounded-md px-3 text-[11.5px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || (visibility === "team" && !teamId)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 text-[11.5px] font-medium text-white transition-colors hover:brightness-110 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Save view
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function SavedViewsView({
  workspaceId,
  currentUserId,
  canWrite,
  canAdmin,
}: {
  workspaceId: string;
  currentUserId: string | null;
  canWrite: boolean;
  canAdmin: boolean;
}) {
  const { views, preferences, loading, error, refresh } =
    useSavedViews(workspaceId);
  const [busyId, setBusyId] = useState<string | null>(null);
  const byView = useMemo(() => preferenceMap(preferences), [preferences]);
  const sorted = useMemo(
    () =>
      [...views].sort((a, b) => {
        const favoriteDelta =
          Number(Boolean(byView.get(b.id)?.is_favorite)) -
          Number(Boolean(byView.get(a.id)?.is_favorite));
        return (
          favoriteDelta ||
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      }),
    [byView, views]
  );

  async function updatePreference(
    viewId: string,
    patch: Partial<Pick<SavedViewPreferenceRow, "is_favorite" | "is_default" | "subscribed">>
  ) {
    setBusyId(viewId);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/views/${viewId}/preference`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(patch),
        }
      );
      if (!response.ok) throw new Error("preference_failed");
      await refresh();
      window.dispatchEvent(new Event(SAVED_VIEWS_CHANGED_EVENT));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(viewId: string) {
    if (!window.confirm("Delete this saved view? Tasks are not affected.")) return;
    setBusyId(viewId);
    try {
      const response = await fetch(
        `/api/workspaces/${workspaceId}/views/${viewId}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("delete_failed");
      await refresh();
      window.dispatchEvent(new Event(SAVED_VIEWS_CHANGED_EVENT));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-[var(--fg-muted)]">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[980px] px-4 py-8 md:px-8 md:py-10">
        <div className="mb-7 max-w-[560px]">
          <h1 className="text-[22px] font-medium tracking-[-0.02em]">
            Saved views
          </h1>
          <p className="mt-1.5 text-[12.5px] leading-5 text-[var(--fg-tertiary)]">
            Reopen a precise task lens without rebuilding filters. Shared “My
            tasks” views resolve to the person opening them.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-[var(--red)]/25 bg-[var(--red)]/10 px-3 py-2 text-[11.5px] text-[var(--red)]">
            {error}
          </div>
        )}

        {sorted.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--border-strong)] px-6 py-12 text-center">
            <Bookmark
              size={20}
              className="mx-auto text-[var(--fg-tertiary)]"
            />
            <h2 className="mt-3 text-[14px] font-medium">No saved views yet</h2>
            <p className="mx-auto mt-1 max-w-[380px] text-[11.5px] leading-5 text-[var(--fg-tertiary)]">
              Open Tasks, choose a scope and filters, then use “Save view” in
              the top bar.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {sorted.map((view) => {
              const preference = byView.get(view.id);
              const mine = view.owner_id === currentUserId;
              return (
                <article
                  key={view.id}
                  className="group rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-4 transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        void updatePreference(view.id, {
                          is_favorite: !preference?.is_favorite,
                        })
                      }
                      disabled={busyId === view.id}
                      className={cn(
                        "focus-ring mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] transition-colors hover:bg-[var(--bg-active)]",
                        preference?.is_favorite
                          ? "text-[var(--yellow)]"
                          : "text-[var(--fg-muted)]"
                      )}
                      title={
                        preference?.is_favorite
                          ? "Remove from sidebar"
                          : "Add to sidebar"
                      }
                    >
                      <Star
                        size={14}
                        fill={preference?.is_favorite ? "currentColor" : "none"}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        currentUserId && openSavedView(view, currentUserId)
                      }
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[14px]">{view.icon || "◫"}</span>
                        <h2 className="truncate text-[13.5px] font-medium text-[var(--fg)]">
                          {view.name}
                        </h2>
                      </div>
                      <p className="mt-1 line-clamp-2 min-h-8 text-[11.5px] leading-4 text-[var(--fg-tertiary)]">
                        {view.description || "A reusable task view."}
                      </p>
                    </button>
                    {((mine &&
                      (view.visibility === "personal" || canWrite)) ||
                      canAdmin) && (
                      <Popover
                        align="end"
                        width={210}
                        trigger={
                          <button
                            type="button"
                            className="focus-ring flex h-7 w-7 items-center justify-center rounded-md text-[var(--fg-tertiary)] opacity-60 transition-colors hover:bg-[var(--bg-active)] hover:text-[var(--fg)] group-hover:opacity-100"
                            aria-label={`View actions for ${view.name}`}
                          >
                            <MoreHorizontal size={14} />
                          </button>
                        }
                      >
                        {(close) => (
                          <div className="p-1">
                            <ViewMenuItem
                              icon={<Trash2 size={12} />}
                              label="Delete view"
                              danger
                              onClick={() => {
                                close();
                                void remove(view.id);
                              }}
                            />
                          </div>
                        )}
                      </Popover>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 border-t border-[var(--border)] pt-3 text-[10.5px] text-[var(--fg-muted)]">
                    <span className="inline-flex items-center gap-1">
                      {visibilityIcon(view.visibility)}
                      {visibilityLabel(view.visibility)}
                    </span>
                    <span>·</span>
                    <span>{view.definition.layout}</span>
                    <span className="ml-auto">
                      Updated{" "}
                      {formatRelative(new Date(view.updated_at).getTime())}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ViewMenuItem({
  icon,
  label,
  onClick,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11.5px] transition-colors hover:bg-[var(--bg-hover)]",
        danger ? "text-[var(--red)]" : "text-[var(--fg-secondary)]"
      )}
    >
      {icon}
      {label}
    </button>
  );
}
